import { Description, Label } from '@oysterjs/ui/fieldset';
import { Radio, RadioField, RadioGroup } from '@oysterjs/ui/radio';

import type { AmsRefInput, EmailAmsFilingTargetInput } from '~/api/graphql';

/** The client an email is logged to, as the dialog hands it back. */
export type ChosenClient = {
  target: AmsRefInput;
  displayName: string;
  filingTarget?: EmailAmsFilingTargetInput | null;
};

/** One row of the list, from the match or from a search. */
export type ClientOption = ChosenClient & {
  detail?: string;
  /** Searched clients may be unknown to Cara, so they are imported before choosing. */
  isSearched: boolean;
};

export const refInputOf = (ref: AmsRefInput): AmsRefInput => ({
  integrationId: ref.integrationId,
  kind: ref.kind,
  ...(ref.accountExternalId != null ? { accountExternalId: ref.accountExternalId } : {}),
  externalId: ref.externalId
});

export const ClientList = (props: {
  label: string;
  options: ClientOption[];
  pickedKey: string | undefined;
  onPick: (option: ClientOption) => void;
}) => (
  <RadioGroup
    value={props.pickedKey}
    onChange={(key: string) => {
      const picked = props.options.find((option) => option.target.externalId === key);
      if (picked) props.onPick(picked);
    }}
    aria-label={props.label}
    className="!space-y-0 divide-y divide-neutral-200 overflow-hidden rounded-lg border border-neutral-200 dark:divide-neutral-700 dark:border-neutral-700"
  >
    {props.options.map((option) => (
      <RadioField
        key={option.target.externalId}
        className={
          option.target.externalId === props.pickedKey
            ? 'bg-sky-50 px-4 py-3 dark:bg-sky-500/10'
            : 'px-4 py-3'
        }
      >
        <Radio
          value={option.target.externalId}
          color="sky"
          className={option.detail ? 'row-span-2' : undefined}
        />
        <Label className="font-medium">{option.displayName}</Label>
        {option.detail && <Description>{option.detail}</Description>}
      </RadioField>
    ))}
  </RadioGroup>
);

export const filingTargetInputOf = (
  target: EmailAmsFilingTargetInput
): EmailAmsFilingTargetInput => ({
  ref: refInputOf(target.ref),
  displayName: target.displayName,
  ...(target.folder
    ? { folder: refInputOf(target.folder), folderDisplayName: target.folderDisplayName }
    : {})
});

export const filingTargetLabel = (target: EmailAmsFilingTargetInput): string =>
  [target.displayName, target.folderDisplayName].filter(Boolean).join(' › ');
